import { FC } from 'react';
import { useSelector } from 'react-redux';
import { pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { OrderItemType } from '@mews-commander/data-access';
import { ApplicationState, OrderItem } from '@mews-commander/types';
import { Container, Grid, Stack } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText } from '@mews/framework';
import { TextKey, RouteName } from '@mews-commander/enums';
import { ConvertedCurrencyValue, LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { useResolveRoute } from '@mews/routing';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getFutureDepositByPastDepositId, getServiceOrder } from '@mews-commander/legacy-entities/src/ducks';
import {
    getRebateByRebatedItemId,
    getRebateOriginalItem,
    isOrderItemRebate,
    isOrderItemRebated,
} from '../../../billing/rebate/selectors/orderItems';
import RebatedTag from '../../../billing/rebate/components/RebatedTag';
import OrderItemLink from '../../../billing/components/OrderItemLink';
import { ConnectedServiceLink, AccountLink, ModalLink, ProfileLink, ServiceOrderLink } from '../../../../components';
import { useAccountingCategories } from '../../../enterpriseRateManagement/components/sections/components/rateForm/fields/accountingCategoriesKey/useAccountingCategories';
import OrderItemStateTag from './OrderItemStateTag';
import OrderItemTypeTag from './OrderItemTypeTag';
import { OrderItemActionLogBillLink } from './OrderItemActionLogBillLink';

interface OrderItemDetailProps {
    orderItem: OrderItem;
}

const DetailRow: FC<React.PropsWithChildren<{}>> = ({ children }) => <Grid templateColumns="1fr 1fr">{children}</Grid>;

const OrderItemDetail = ({ orderItem }: OrderItemDetailProps) => {
    const serviceOrder = useSelector((state: ApplicationState) =>
        getServiceOrder(state, { id: orderItem.serviceOrderId })
    );

    const resolveRoute = useResolveRoute();

    const enterpriseId = useCurrentEnterpriseId();

    const { data } = useAccountingCategories(enterpriseId ? [enterpriseId] : []);

    const accountingCategory = pipe(
        data,
        option.fromNullable,
        option.chainNullableK(({ accountingCategories }) => accountingCategories),
        option.chain(array.findFirst(({ id }) => id === orderItem.assignedAccountingCategoryId)),
        option.toNullable
    );

    const accountingCategoryLink = accountingCategory
        ? resolveRoute(RouteName.AccountingCategoryDetail, { id: accountingCategory.id })
        : '';

    const isRebated = isOrderItemRebated(orderItem);
    const isRebate = isOrderItemRebate(orderItem);
    const originalItem = useSelector((state: ApplicationState) => getRebateOriginalItem(state, { orderItem }));
    const rebateItem = useSelector((state: ApplicationState) =>
        isRebated ? getRebateByRebatedItemId(state, { rebatedItemId: orderItem.id }) : undefined
    );

    const pastDepositId =
        orderItem.data.discriminator === OrderItemType.Deposit ? orderItem.data.value.pastDepositId : undefined;

    const futureDeposit = useSelector((state: ApplicationState) =>
        orderItem.data.discriminator === OrderItemType.Deposit && !pastDepositId
            ? getFutureDepositByPastDepositId(state, { pastDepositId: orderItem.id })
            : undefined
    );

    return (
        <Container padding="200">
            <Stack vertical spacing="300">
                <DetailRow>
                    <LocalizedText textKey={TextKey.Type} />
                    <Stack>
                        {/* @ts-expect-error */}
                        <OrderItemTypeTag orderItem={orderItem} />
                    </Stack>
                </DetailRow>
                <DetailRow>
                    <LocalizedText textKey={TextKey.StateCondition} />
                    <Stack itemAlignment="center" spacing="100">
                        <OrderItemStateTag orderItemState={orderItem.accountingState} />
                        {orderItem.isRebated ? <RebatedTag /> : null}
                    </Stack>
                </DetailRow>
                <DetailRow>
                    <LocalizedText textKey={TextKey.Amount} />
                    <ConvertedCurrencyValue amount={orderItem.totalAmount} />
                </DetailRow>
                {pastDepositId ? (
                    <DetailRow>
                        <LocalizedText textKey={TextKey.PastDeposit} />
                        <OrderItemLink orderItemId={pastDepositId} />
                    </DetailRow>
                ) : null}
                {futureDeposit ? (
                    <DetailRow>
                        <LocalizedText textKey={TextKey.FutureDeposit} />
                        <OrderItemLink orderItemId={futureDeposit.id} />
                    </DetailRow>
                ) : null}
                {isRebate && originalItem ? (
                    <DetailRow>
                        <LocalizedText textKey={TextKey.RebatedItem} />
                        <OrderItemLink orderItemId={originalItem.id} />
                    </DetailRow>
                ) : null}
                {isRebated && rebateItem ? (
                    <DetailRow>
                        <LocalizedText textKey={TextKey.Rebate} />
                        <OrderItemLink orderItemId={rebateItem.id} />
                    </DetailRow>
                ) : null}
                <DetailRow>
                    <LocalizedText textKey={TextKey.Payer} />
                    <AccountLink id={orderItem.ownerId} />
                </DetailRow>
                <DetailRow>
                    <LocalizedText textKey={TextKey.Bill} />
                    {orderItem.billId ? <OrderItemActionLogBillLink billId={orderItem.billId} /> : UnicodeEntity.Dash}
                </DetailRow>
                <DetailRow>
                    <LocalizedText textKey={TextKey.Service} />
                    {serviceOrder ? <ConnectedServiceLink serviceId={serviceOrder.serviceId} /> : UnicodeEntity.Dash}
                </DetailRow>
                <DetailRow>
                    <LocalizedText
                        textKey={
                            serviceOrder?.data.discriminator === 'Reservation'
                                ? TextKey.Reservation
                                : TextKey.ServiceOrder
                        }
                    />
                    {serviceOrder ? <ServiceOrderLink serviceOrderId={serviceOrder.id} /> : UnicodeEntity.Dash}
                </DetailRow>
                <DetailRow>
                    <LocalizedText textKey={TextKey.AccountingCategory} />
                    {accountingCategory ? (
                        <ModalLink
                            data-test-accounting-category-id={accountingCategory.id}
                            url={accountingCategoryLink}
                        >
                            {accountingCategory.name}
                        </ModalLink>
                    ) : (
                        <LocalizedText textKey={TextKey.None} />
                    )}
                </DetailRow>
                <DetailRow>
                    <LocalizedText textKey={TextKey.Created} />
                    <LocalizedDateTimeFromUtc utcString={orderItem.createdUtc} />
                </DetailRow>
                <DetailRow>
                    <LocalizedText textKey={TextKey.CreatedBy} />
                    <ProfileLink profileId={orderItem.creatorProfileId} />
                </DetailRow>
                <DetailRow>
                    <LocalizedText textKey={TextKey.Updated} />
                    <LocalizedDateTimeFromUtc utcString={orderItem.updatedUtc} />
                </DetailRow>
                <DetailRow>
                    <LocalizedText textKey={TextKey.UpdatedBy} />
                    <ProfileLink profileId={orderItem.updaterProfileId} />
                </DetailRow>
                {orderItem.canceledUtc ? (
                    <DetailRow>
                        <LocalizedText textKey={TextKey.Canceled} />
                        <LocalizedDateTimeFromUtc utcString={orderItem.canceledUtc} />
                    </DetailRow>
                ) : null}
            </Stack>
        </Container>
    );
};

export default OrderItemDetail;
